import React from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import {StoreDeliveriesSection} from '../components/deliveries/StoreDeliveriesSection';
import {useStoreDetails} from '../hooks/useStoreDetails';
import {AppText, AppLabelText} from '../../../Presentation/components/ui/Typography';
import {ArrowRight, Package} from 'lucide-react';
import {PermissionDeniedCard} from '../../../Presentation/components/common';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';

/**
 * صفحة مستقلة لسجل تسليمات متجر واحد.
 * يمكن الوصول إليها عبر /dashboard/stores/:storeId/deliveries
 */
const StoreDeliveriesPage: React.FC = () => {
  const {storeId} = useParams<{storeId: string}>();
  const navigate = useNavigate();
  const {hasPermission} = useAuthorization();

  const {store, loading, error} = useStoreDetails({
    storeId: storeId ?? null,
    isOpen: true,
    stores: undefined
  });

  if (!hasPermission(PERMISSIONS.READ_STORES) && !hasPermission(PERMISSIONS.MANAGE_STORES)) {
    return (
      <SidebarLayout>
        <div className="p-6 min-h-screen rounded-xl bg-[#F2F3F5]">
          <PermissionDeniedCard message="ليس لديك صلاحيات للوصول إلى سجل التسليمات." />
        </div>
      </SidebarLayout>
    );
  }

  const goBack = () => navigate('/dashboard/stores');

  if (!storeId) {
    return (
      <SidebarLayout>
        <div className="p-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
            <AppText className="text-amber-800">لم يتم تحديد المتجر.</AppText>
            <button
              type="button"
              onClick={goBack}
              className="mt-3 text-sm text-amber-700 hover:text-amber-900 font-medium"
            >
              العودة إلى إدارة المتاجر
            </button>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  if (loading) {
    return (
      <SidebarLayout>
        <div className="p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
            <span className="mr-3 text-gray-600">جاري تحميل بيانات المتجر...</span>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  if (error || !store) {
    return (
      <SidebarLayout>
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AppText className="text-red-700">{error || 'لم يتم العثور على المتجر.'}</AppText>
            <button
              type="button"
              onClick={goBack}
              className="mt-3 text-sm text-red-600 hover:text-red-800 font-medium"
            >
              العودة إلى إدارة المتاجر
            </button>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header: back + store name */}
        <div className="mb-6 flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm font-medium"
          >
            <ArrowRight className="w-4 h-4" />
            العودة إلى إدارة المتاجر
          </button>
          <span className="text-gray-400">|</span>
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-brand-600" />
            <AppLabelText className="text-gray-900 font-semibold">
              سجل التسليمات — {store.name}
            </AppLabelText>
          </div>
        </div>

        <StoreDeliveriesSection
          storeId={store.id.toString()}
          storeName={store.name}
        />
      </div>
    </SidebarLayout>
  );
};

export default StoreDeliveriesPage;
